// Meta-Ads-Tab: Wochenreport aus der DIREKTEN Meta-Marketing-API (claude_meta_weekly.mjs).
// Pro Werbekonto: 7T-Aggregat + Vorwoche + 30T, Kampagnen, beste Platzierungen, beste Anzeigen,
// beste Bild-Assets, Käufe via Pixel + Kosten je Kauf (CpP) + Claude-Insight.
import type { MarketingDeps } from "./marketing.js";

interface Tot {
  spend: number; impressions: number; clicks: number; ctr: number; cpc: number;
  reach?: number; frequency?: number; purchases: number; value: number; roas: number; cpp: number;
}
interface Camp extends Tot { name: string }
interface Placement { placement: string; spend: number; impressions: number; clicks: number; ctr: number; purchases: number }
interface Ad { name: string; spend: number; impressions: number; ctr: number; purchases: number; cpp: number }
interface Img { name?: string; hash?: string; spend: number; impressions: number; clicks: number; ctr: number; purchases: number }
interface Account {
  label: string; totals7: Tot; totalsPrev7: Tot | null; totals30: Tot | null;
  campaigns: Camp[]; placements: Placement[]; topAds: Ad[]; topImages: Img[];
}
interface MetaSummary {
  empty?: boolean; generated_at?: string; report_at?: string;
  window?: { since7: string; until: string; sincePrev: string; untilPrev: string; since30: string };
  accounts?: Record<string, Account>;
  insight?: { summary: string; recommendations: string[] } | null;
}

const eur = (n: number): string => (n ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 0 }) + " €";
const eur2 = (n: number): string => (n ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const num = (n: number): string => (n ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 0 });
const x = (n: number): string => (n ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function el(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e;
}
function table(head: string[], rows: Array<Array<string>>): HTMLTableElement {
  const t = document.createElement("table"); t.className = "om-table";
  const hr = t.createTHead().insertRow();
  for (const h of head) hr.appendChild(el("th", undefined, h));
  const tb = t.createTBody();
  for (const r of rows) { const tr = tb.insertRow(); for (const c of r) tr.insertCell().textContent = c; }
  return t;
}

export function initMetaTab(m: HTMLElement, deps: MarketingDeps): void {
  const host = m.querySelector("[data-meta]") as HTMLElement | null;
  const msg = m.querySelector('[data-msg="meta"]') as HTMLElement | null;
  if (!host) return;

  const load = async (): Promise<void> => {
    host.innerHTML = '<p class="dim">Lade …</p>';
    const r = await deps.api<MetaSummary>("/api/marketing/meta");
    const d = r.data;
    if (!d || d.empty || !d.accounts || !Object.keys(d.accounts).length) {
      host.innerHTML = '<p class="dim">Noch keine Meta-Daten. Über „↻ Meta-Daten ziehen“ starten (direkte Meta-Marketing-API, letzte 7 Tage).</p>';
      return;
    }
    host.textContent = "";

    const w = d.window;
    if (w) host.appendChild(el("p", "dim", `Zeitraum: ${w.since7} – ${w.until} (Vorwoche ${w.sincePrev} – ${w.untilPrev})`));

    // Insight
    if (d.insight) {
      const det = document.createElement("details"); det.className = "perf-insight"; det.open = true;
      const sum = document.createElement("summary");
      sum.appendChild(el("span", "perf-insight__title", "💡 Meta-Insight"));
      const when = d.report_at || d.generated_at;
      if (when) sum.appendChild(el("span", "perf-insight__date", "Stand " + new Date(when).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })));
      const rc = d.insight.recommendations?.length ?? 0;
      sum.appendChild(el("span", "perf-insight__hint", rc ? `${rc} Empfehlungen` : "ansehen"));
      det.appendChild(sum);
      const body = el("div", "perf-insight__body");
      body.appendChild(el("p", undefined, d.insight.summary));
      if (rc) { const ul = document.createElement("ul"); for (const rec of d.insight.recommendations) ul.appendChild(el("li", undefined, rec)); body.appendChild(ul); }
      det.appendChild(body);
      host.appendChild(det);
    }

    // Pro Werbekonto
    for (const acc of Object.values(d.accounts)) {
      const h = el("h3", "om-ads-h");
      const ic = el("span", "om-ads-logo");
      ic.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><defs><linearGradient id="ig" x1="0" y1="1" x2="1" y2="0"><stop offset="0" stop-color="#FEDA75"/><stop offset=".5" stop-color="#D62976"/><stop offset="1" stop-color="#4F5BD5"/></linearGradient></defs><path fill="url(#ig)" d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 01-1.38-.9 3.7 3.7 0 01-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.31-1.46.72-2.13 1.38A5.9 5.9 0 00.63 4.14c-.3.76-.5 1.64-.56 2.91C.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.67.66 1.34 1.07 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.9 5.9 0 002.13-1.38 5.9 5.9 0 001.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.9 5.9 0 00-1.38-2.13A5.9 5.9 0 0019.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0z"/><path fill="url(#ig)" d="M12 5.84A6.16 6.16 0 105.84 12 6.16 6.16 0 0012 5.84zM12 16a4 4 0 114-4 4 4 0 01-4 4z"/><circle fill="url(#ig)" cx="18.41" cy="5.59" r="1.44"/></svg>';
      h.appendChild(ic);
      h.appendChild(document.createTextNode("Meta · " + acc.label));
      host.appendChild(h);

      const t = acc.totals7;
      host.appendChild(el("p", "om-ads-reliable",
        `7 Tage: Spend ${eur2(t.spend)} · ${num(t.purchases)} Käufe · CTR ${x(t.ctr)} % · CPC ${eur2(t.cpc)} · Kosten/Kauf ${t.cpp ? eur2(t.cpp) : "—"} · ROAS ${x(t.roas)}`));

      // 7T vs Vorwoche vs 30T
      const rows: string[][] = [["letzte 7 T", eur(t.spend), num(t.impressions), x(t.ctr), eur2(t.cpc), num(t.purchases), t.cpp ? eur(t.cpp) : "—", x(t.roas)]];
      if (acc.totalsPrev7) { const p = acc.totalsPrev7; rows.push(["Vorwoche", eur(p.spend), num(p.impressions), x(p.ctr), eur2(p.cpc), num(p.purchases), p.cpp ? eur(p.cpp) : "—", x(p.roas)]); }
      if (acc.totals30) { const p = acc.totals30; rows.push(["letzte 30 T", eur(p.spend), num(p.impressions), x(p.ctr), eur2(p.cpc), num(p.purchases), p.cpp ? eur(p.cpp) : "—", x(p.roas)]); }
      host.appendChild(table(["Zeitraum", "Spend", "Impr.", "CTR %", "CPC", "Käufe", "Kosten/Kauf", "ROAS"], rows));

      if (acc.campaigns?.length) {
        host.appendChild(el("h4", "om-ads-sub", "Kampagnen (7 T)"));
        host.appendChild(table(["Kampagne", "Spend", "CTR %", "Käufe", "Kosten/Kauf", "ROAS"],
          acc.campaigns.map((c) => [c.name, eur(c.spend), x(c.ctr), num(c.purchases), c.cpp ? eur(c.cpp) : "—", x(c.roas)])));
      }
      if (acc.placements?.length) {
        host.appendChild(el("h4", "om-ads-sub", "Beste Platzierungen (7 T, nach Spend)"));
        host.appendChild(table(["Platzierung", "Spend", "Impr.", "CTR %", "Käufe"],
          acc.placements.map((p) => [p.placement, eur(p.spend), num(p.impressions), x(p.ctr), num(p.purchases)])));
      }
      if (acc.topAds?.length) {
        host.appendChild(el("h4", "om-ads-sub", "Top-Anzeigen (7 T)"));
        host.appendChild(table(["Anzeige", "Spend", "CTR %", "Käufe", "Kosten/Kauf"],
          acc.topAds.map((a) => [a.name, eur(a.spend), x(a.ctr), num(a.purchases), a.cpp ? eur(a.cpp) : "—"])));
      }
      if (acc.topImages?.length) {
        host.appendChild(el("h4", "om-ads-sub", "Beste Bild-Assets (7 T, nach Reichweite)"));
        host.appendChild(table(["Bild", "Impr.", "CTR %", "Käufe"],
          acc.topImages.map((i) => [i.name || i.hash || "—", num(i.impressions), x(i.ctr), num(i.purchases)])));
      }
    }
  };

  m.querySelector('[data-action="run-meta"]')?.addEventListener("click", () => {
    void (async () => {
      if (msg) msg.textContent = "Starte Meta-Pull …";
      const r = await deps.api<{ stamp?: string }>("/api/marketing/motors/meta_weekly/run", "POST");
      if (msg) msg.textContent = r.status === 200 ? "Läuft — Tab in ~1 Min neu öffnen." : r.status === 409 ? "Läuft bereits." : `Fehler (${r.status})`;
    })();
  });

  const tabBtn = m.querySelector('[data-tab="meta"]');
  tabBtn?.addEventListener("click", () => {
    void deps.api("/api/marketing/meta/seen", "POST");
    const badge = tabBtn.querySelector(".tab-badge") as HTMLElement | null;
    if (badge) { badge.textContent = "0"; badge.hidden = true; }
  });

  void load();
}
